<template>
  <div class="auth-container">
    <div class="auth-card">
      <!-- 左侧装饰图片区域 - PC端显示 -->
      <div class="auth-left" aria-hidden="true">
        <img src="/static/images/login-image-3.png" class="decor-img third-img" alt="" />
        <img src="/static/images/login-image-2.png" class="decor-img second-img" alt="" />
        <img src="/static/images/login-image-1.png" class="decor-img first-img" alt="" />
      </div>

      <!-- 右侧表单区域 -->
      <div class="auth-right">
        <!-- 头部区域 -->
        <div class="auth-header">
          <img src="/static/images/user.png" alt="用户图标" class="user-icon" />
          <div class="header-title">重置密码</div>
        </div>

        <!-- 分隔线 -->
        <div class="header-divider"></div>

        <!-- 步骤指示器 -->
        <div class="step-indicator">
          <div class="step-item" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
            <div class="step-number">{{ currentStep > 1 ? '✓' : '1' }}</div>
            <div class="step-label">验证身份</div>
          </div>
          <div class="step-line" :class="{ active: currentStep > 1 }"></div>
          <div class="step-item" :class="{ active: currentStep === 2, completed: currentStep > 2 }">
            <div class="step-number">{{ currentStep > 2 ? '✓' : '2' }}</div>
            <div class="step-label">设置密码</div>
          </div>
          <div class="step-line" :class="{ active: currentStep > 2 }"></div>
          <div class="step-item" :class="{ active: currentStep === 3 }">
            <div class="step-number">3</div>
            <div class="step-label">完成</div>
          </div>
        </div>

        <!-- 表单区域 -->
        <el-form ref="resetForm" :model="form" :rules="rules" class="auth-form">

          <!-- 第一步：验证手机号 -->
          <div v-show="currentStep === 1" class="step-content">
            <div class="form-item">
              <span class="form-label">手机号:</span>
              <el-form-item prop="phone">
                <el-input v-model="form.phone" placeholder="请输入注册手机号" maxlength="11" clearable />
              </el-form-item>
            </div>

            <div class="form-item">
              <span class="form-label">验证码:</span>
              <el-form-item prop="verifyCode">
                <div class="input-with-btn">
                  <el-input v-model="form.verifyCode" placeholder="请输入验证码" maxlength="6" clearable />
                  <el-button class="verify-btn" type="primary" plain :disabled="countdown > 0 || !canSendCode"
                    @click="sendVerifyCode">
                    {{ countdown > 0 ? countdown + 's' : '获取验证码' }}
                  </el-button>
                </div>
              </el-form-item>
            </div>

            <div class="form-actions">
              <el-button type="primary" class="submit-btn" :loading="loading" @click="validateCode">
                验证并继续
              </el-button>
            </div>
          </div>

          <!-- 第二步：设置新密码 -->
          <div v-show="currentStep === 2" class="step-content">
            <div class="form-item">
              <span class="form-label">新密码:</span>
              <el-form-item prop="newPassword">
                <el-input v-model="form.newPassword" type="password" placeholder="请设置新密码（6-20位）" show-password clearable
                  @input="checkPasswordStrength" />
              </el-form-item>
            </div>
            <div class="password-strength" v-if="form.newPassword">
              <div class="strength-bar">
                <div class="strength-fill" :class="strengthClass"></div>
              </div>
              <span class="strength-text" :class="strengthClass">{{ strengthText }}</span>
            </div>

            <div class="form-item">
              <span class="form-label">确认密码:</span>
              <el-form-item prop="confirmPassword">
                <el-input v-model="form.confirmPassword" type="password" placeholder="请再次输入新密码" show-password
                  clearable />
              </el-form-item>
            </div>

            <div class="form-actions btn-group">
              <el-button class="submit-btn secondary" @click="prevStep">
                上一步
              </el-button>
              <el-button type="primary" class="submit-btn" :loading="loading" @click="submitChangePassword">
                确认重置
              </el-button>
            </div>
          </div>

          <!-- 第三步：完成 -->
          <div v-show="currentStep === 3" class="step-content success-content">
            <div class="success-icon">
              <i class="el-icon-circle-check"></i>
            </div>
            <h3 class="success-title">密码重置成功</h3>
            <p class="success-message">您的密码已成功重置，请使用新密码登录</p>
            <el-button type="primary" class="submit-btn" @click="goToLogin">
              立即登录
            </el-button>
          </div>
        </el-form>

        <!-- 底部链接 -->
        <div class="form-links" v-if="currentStep < 3">
          <router-link to="/login" class="link-primary">返回登录</router-link>
        </div>
      </div>
    </div>

    <!-- 版权信息 -->
    <div class="auth-copyright">
      Copyright &copy; 2023 北京新知堂剑桥少儿英语
    </div>
  </div>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'ResetPassword',
  data () {
    return {
      currentStep: 1,
      loading: false,
      countdown: 0,
      countdownTimer: null,
      canSendCode: true,
      passwordStrength: 0,
      form: {
        phone: '',
        verifyCode: '',
        newPassword: '',
        confirmPassword: ''
      },
      rules: {
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        verifyCode: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { pattern: /^\d{6}$/, message: '验证码为6位数字', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请设置新密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度为6-20位', trigger: 'blur' },
          { pattern: /(?=.*[0-9])(?=.*[a-zA-Z])/, message: '密码必须包含字母和数字', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入密码', trigger: 'blur' },
          { validator: this.validateConfirmPassword, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    strengthClass () {
      if (this.passwordStrength <= 1) return 'weak'
      if (this.passwordStrength <= 2) return 'medium'
      return 'strong'
    },
    strengthText () {
      if (this.passwordStrength <= 1) return '弱'
      if (this.passwordStrength <= 2) return '中'
      return '强'
    }
  },
  beforeDestroy () {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }
  },
  methods: {
    validateConfirmPassword (rule, value, callback) {
      if (value !== this.form.newPassword) {
        callback(new Error('两次密码不一致'))
      } else {
        callback()
      }
    },

    sendVerifyCode () {
      var self = this
      this.$refs.resetForm.validateField('phone', function (errorMsg) {
        if (errorMsg) {
          return
        }

        self.loading = true
        // MVC 接口使用 form-urlencoded 格式
        const params = new URLSearchParams()
        params.append('account', self.form.phone)

        self.$axios.post(api.sendPasswordResetCode, params, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        }).then(function (response) {
          var result = response.data.result || response.data
          if (result.code) {
            self.$message({
              message: '验证码已发送',
              type: 'success'
            })
            self.startCountdown()
          } else {
            self.$message({
              message: '验证码发送失败',
              type: 'error'
            })
          }
        }).catch(function (error) {
          var errResult = error.response && (error.response.data.result || error.response.data)
          self.$message({
            message: errResult && errResult.message ? errResult.message : '验证码发送失败',
            type: 'error'
          })
        }).finally(function () {
          self.loading = false
        })
      })
    },

    startCountdown () {
      var self = this
      this.countdown = 60
      this.canSendCode = false
      this.countdownTimer = setInterval(function () {
        if (self.countdown > 0) {
          self.countdown--
        } else {
          clearInterval(self.countdownTimer)
          self.canSendCode = true
        }
      }, 1000)
    },

    checkPasswordStrength () {
      var password = this.form.newPassword
      var strength = 0

      if (!password) {
        this.passwordStrength = 0
        return
      }

      if (password.length >= 8) strength++
      if (password.length >= 12) strength++
      if (/[0-9]/.test(password)) strength++
      if (/[a-zA-Z]/.test(password)) strength++
      if (/[^a-zA-Z0-9]/.test(password)) strength++

      this.passwordStrength = Math.min(Math.floor(strength / 2), 3)
    },

    validateCode () {
      var self = this
      // 手动验证第一步需要的字段，避免验证第二步字段
      var fields = ['phone', 'verifyCode']
      var errors = []
      var checked = 0

      fields.forEach(function (field) {
        self.$refs.resetForm.validateField(field, function (errorMsg) {
          checked++
          if (errorMsg) {
            errors.push(errorMsg)
          }
          // 所有字段都检查完成后执行
          if (checked === fields.length) {
            if (errors.length > 0) {
              return
            }

            self.loading = true
            // MVC 接口使用 form-urlencoded 格式
            const params = new URLSearchParams()
            params.append('code', self.form.verifyCode)

            self.$axios.post(api.validatePasswordResetCode, params, {
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            }).then(function (response) {
              // 后端返回 Status: 200(成功), 400(超时), 500(无效)
              var result = response.data.result || response.data
              if (result.status === 200) {
                self.currentStep = 2
                self.$message({
                  message: '验证通过',
                  type: 'success'
                })
              } else {
                self.$message({
                  message: result.message || '验证码错误',
                  type: 'error'
                })
              }
            }).catch(function (error) {
              var errResult = error.response && (error.response.data.result || error.response.data)
              self.$message({
                message: errResult && errResult.message ? errResult.message : '验证失败',
                type: 'error'
              })
            }).finally(function () {
              self.loading = false
            })
          }
        })
      })
    },

    prevStep () {
      this.currentStep = 1
    },

    submitChangePassword () {
      var self = this
      this.$refs.resetForm.validate(function (valid) {
        if (!valid) {
          return
        }

        self.loading = true
        // MVC 接口使用 form-urlencoded 格式
        const params = new URLSearchParams()
        params.append('account', self.form.phone)
        params.append('code', self.form.verifyCode)
        params.append('pwd', self.form.newPassword)

        self.$axios.post(api.passwordReset, params, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        }).then(function (response) {
          // 后端返回 status: 0(账号或验证码无效), 1(验证码超时), 2(成功)
          if (response.data.result.status === 2) {
            self.currentStep = 3
            self.$message({
              message: '密码重置成功',
              type: 'success'
            })
          } else if (response.data.result.status === 1) {
            self.$message({
              message: '验证码超时',
              type: 'error'
            })
          } else {
            self.$message({
              message: '账号或验证码无效',
              type: 'error'
            })
          }
        }).catch(function (error) {
          self.$message({
            message: error.response && error.response.data && error.response.data.result.message ? error.response.data.result.message : '密码重置失败',
            type: 'error'
          })
        }).finally(function () {
          self.loading = false
        })
      })
    },

    goToLogin () {
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
/* ========================================
   基础样式（移动端优先）
   ======================================== */
.auth-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #00CCFF;
  padding: 15px;
  box-sizing: border-box;
}

.auth-card {
  width: 100%;
  max-width: 400px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 25px 20px;
  box-sizing: border-box;
}

/* 左侧装饰图片 - 移动端隐藏 */
.auth-left {
  display: none;
}

/* 头部区域 */
.auth-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 15px;
}

.user-icon {
  width: 50px;
  height: 50px;
  margin-right: 15px;
}

.header-title {
  font-size: 22px;
  font-weight: bold;
  color: #000;
  letter-spacing: 4px;
}

/* 分隔线 */
.header-divider {
  height: 1px;
  background: #cccccc;
  margin-bottom: 20px;
}

/* 步骤指示器 */
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.step-number {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #e4e7ed;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 5px;
}

.step-item.active .step-number {
  background: #30adfa;
  color: #fff;
}

.step-item.completed .step-number {
  background: #67c23a;
  color: #fff;
}

.step-label {
  font-size: 12px;
  color: #999;
}

.step-item.active .step-label {
  color: #30adfa;
}

.step-line {
  width: 30px;
  height: 2px;
  background: #e4e7ed;
  margin: 0 8px;
  margin-bottom: 18px;
}

.step-line.active {
  background: #30adfa;
}

/* 表单区域 */
.auth-form {
  width: 100%;
}

.form-item {
  margin-bottom: 20px;
}

.form-item .el-form-item {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 16px;
  color: #000;
  margin-bottom: 8px;
}

.form-item .el-input {
  height: 44px;
}

.form-item .el-input>>>.el-input__inner {
  height: 44px;
  line-height: 44px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0 15px;
}

.input-with-btn {
  display: flex;
  gap: 10px;
}

.input-with-btn .el-input {
  flex: 1;
}

.verify-btn {
  white-space: nowrap;
  min-width: 100px;
  height: 40px;
  border-radius: 4px;
  font-size: 14px;
}

/* 密码强度 */
.password-strength {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: -10px 0 15px 0;
}

.strength-bar {
  flex: 1;
  height: 4px;
  background: #e4e7ed;
  border-radius: 2px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  border-radius: 2px;
}

.strength-fill.weak {
  width: 33.33%;
  background: #f56c6c;
}

.strength-fill.medium {
  width: 66.66%;
  background: #e6a23c;
}

.strength-fill.strong {
  width: 100%;
  background: #67c23a;
}

.strength-text {
  font-size: 12px;
}

.strength-text.weak {
  color: #f56c6c;
}

.strength-text.medium {
  color: #e6a23c;
}

.strength-text.strong {
  color: #67c23a;
}

/* 链接区域 */
.form-links {
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
}

.link-primary {
  color: #30adfa;
  text-decoration: none;
}

.link-primary:hover {
  text-decoration: underline;
}

/* 提交按钮 */
.form-actions {
  text-align: center;
  margin-top: 25px;
}

.submit-btn {
  width: 100%;
  max-width: 260px;
  height: 44px;
  font-size: 18px;
  border-radius: 5px;
  background: #30adfa;
  border: none;
}

.submit-btn:hover {
  opacity: 0.9;
}

.submit-btn.secondary {
  background: #fff;
  color: #30adfa;
  border: 1px solid #30adfa;
}

.btn-group {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.btn-group .submit-btn {
  flex: 1;
  max-width: 150px;
}

/* 成功状态 */
.success-content {
  text-align: center;
  padding: 20px 0;
}

.success-icon {
  font-size: 50px;
  color: #67c23a;
  margin-bottom: 15px;
}

.success-title {
  font-size: 18px;
  color: #333;
  margin: 0 0 10px 0;
}

.success-message {
  font-size: 14px;
  color: #999;
  margin: 0 0 20px 0;
}

/* 版权信息 */
.auth-copyright {
  margin-top: 20px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  text-align: center;
}

/* ========================================
   中等屏幕 (768px - 991px) - 平板
   ======================================== */
@media (min-width: 768px) {
  .auth-container {
    padding: 20px;
  }

  .auth-card {
    display: flex;
    max-width: 90%;
    padding: 0;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }

  .auth-left {
    display: block;
    position: relative;
    width: 35%;
    min-height: 400px;
    overflow: hidden;
    /* background: linear-gradient(135deg, #00b4db 0%, #0083b0 100%); */
  }

  .decor-img {
    position: absolute;
    max-width: 130px;
    height: auto;
  }

  .first-img {
    top: 80px;
    left: 15%;
  }

  .second-img {
    top: 80px;
    left: 55%;
  }

  .third-img {
    top: 80px;
    left: 75%;
  }

  .auth-right {
    width: 55%;
    padding: 30px 25px;
    box-sizing: border-box;
  }

  .auth-header {
    justify-content: flex-start;
    padding-bottom: 0;
  }

  .user-icon {
    width: 60px;
    height: 60px;
    margin-right: 20px;
  }

  .header-title {
    font-size: 22px;
    letter-spacing: 8px;
  }

  .header-divider {
    max-width: 356px;
    margin: 20px 0;
  }

  .step-indicator {
    justify-content: flex-start;
    padding-left: 20px;
    margin-bottom: 25px;
  }

  .form-item {
    display: flex;
    align-items: center;
    max-width: 356px;
  }

  .form-label {
    display: inline-block;
    width: 70px;
    font-size: 18px;
    margin-bottom: 0;
    flex-shrink: 0;
  }

  .form-item .el-form-item {
    flex: 1;
  }

  .form-item .el-input {
    flex: 1;
    height: 40px;
  }

  .form-item .el-input>>>.el-input__inner {
    height: 40px;
    line-height: 40px;
  }

  .password-strength {
    margin: -10px 0 15px 70px;
  }

  .form-links {
    text-align: left;
    padding-left: 70px;
    font-size: 16px;
  }

  .form-actions {
    text-align: left;
    padding-left: 70px;
    margin-top: 30px;
  }

  .submit-btn {
    width: 200px;
    height: 40px;
  }

  .btn-group {
    gap: 10px;
  }

  .btn-group .submit-btn {
    max-width: 120px;
  }
}

/* ========================================
   大屏幕 (992px+) - 桌面
   ======================================== */
@media (min-width: 992px) {
  .auth-container {
    padding: 30px;
  }

  .auth-card {
    max-width: 1000px;
    height: 540px;
    margin-top: 60px;
    border-radius: 20px;
  }

  .auth-left {
    width: calc(60% - 20px);
    min-height: 540px;
    border-radius: 20px 0 0 20px;
  }

  .decor-img {
    max-width: none;
  }

  .first-img {
    top: 90px;
    left: 55px;
  }

  .second-img {
    top: 90px;
    left: 200px;
  }

  .third-img {
    top: 90px;
    left: 270px;
  }

  .auth-right {
    width: 40%;
    min-width: 370px;
    padding: 30px 30px 30px 20px;
  }

  .user-icon {
    width: 74px;
    height: 74px;
  }

  .header-title {
    line-height: 74px;
  }

  .header-divider {
    width: 356px;
  }

  .form-item {
    width: 356px;
    margin-top: 25px;
  }

  .form-label {
    width: 90px;
  }

  .password-strength {
    margin-left: 90px;
  }

  .form-links {
    padding-left: 110px;
  }

  .form-actions {
    padding-left: 50px;
    margin-top: 30px;
  }

  .submit-btn {
    width: 260px;
    height: 40px;
  }

  .btn-group .submit-btn {
    max-width: 125px;
  }

  .auth-copyright {
    margin-top: 30px;
  }
}

@media (min-width: 1200px) {
  .auth-card {
    margin-top: 80px;
  }
}

@media (max-width: 767px) and (orientation: landscape) {
  .auth-container {
    padding: 10px;
  }

  .auth-card {
    padding: 15px;
  }

  .user-icon {
    width: 40px;
    height: 40px;
    margin-right: 10px;
  }

  .header-title {
    font-size: 18px;
  }

  .form-item {
    margin-bottom: 15px;
  }

  .form-item .el-input {
    height: 40px;
  }

  .form-item .el-input>>>.el-input__inner {
    height: 40px;
    line-height: 40px;
  }

  .submit-btn {
    height: 40px;
    font-size: 16px;
  }

  .auth-copyright {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/view/auth/ResetPassword.vue